import { contextMatches, normalizeContext } from '$domain/context-match';
import type { PlanContext } from '$domain/context-match';
import type { PayloadOf, TypedEvent } from '$domain/events';
import type { Reducer } from '$domain/reducers/reduce';

export interface Plan {
  planId: string;
  trigger: string;
  action: string;
  context: PlanContext;
  directionRef: string | null;
  origin: 'onboarding' | 'selfGenerated';
  archived: boolean;
  /** How many times this plan has been shown on a resolution screen. */
  surfacedCount: number;
  /** How many times it was actually chosen. */
  chosenCount: number;
  /** Log id of the most recent surfacing, for ordering. */
  lastSurfacedId: string | null;
}

export interface PlansState {
  readonly byId: ReadonlyMap<string, Plan>;
}

export interface PlanRevisionThresholds {
  readonly minSurfacings: number;
}

const EMPTY: PlansState = { byId: new Map() };

export const plansReducer: Reducer<PlansState> = {
  name: 'plans',
  initial: EMPTY,
  handles: ['plan.created', 'plan.revised', 'plan.archived', 'plan.surfaced', 'catch.resolved'],
  apply(state, event) {
    if (event.type === 'plan.created') {
      const payload = event.payload as PayloadOf<'plan.created'>;
      // The first creation is the fact: merged device logs must converge rather
      // than letting a duplicate creation rewrite an existing plan.
      if (state.byId.has(payload.planId)) return state;

      const plan: Plan = {
        planId: payload.planId,
        trigger: payload.trigger,
        action: payload.action,
        context: normalizeContext(payload.context),
        directionRef: payload.directionRef ?? null,
        origin: payload.origin,
        archived: false,
        surfacedCount: 0,
        chosenCount: 0,
        lastSurfacedId: null,
      };
      const byId = new Map(state.byId);
      byId.set(plan.planId, plan);
      return { byId };
    }

    if (event.type === 'plan.revised') {
      const payload = event.payload as PayloadOf<'plan.revised'>;
      const existing = state.byId.get(payload.planId);
      // A revision adjusts an existing hypothesis; it cannot honestly create one.
      if (!existing) return state;

      // Revision keeps the same plan's offer and choice history, which is the
      // signal spec §6.3 uses to decide whether the adjustment helped.
      const updated: Plan = {
        ...existing,
        trigger: payload.trigger,
        action: payload.action,
        context: normalizeContext(payload.context),
        directionRef: payload.directionRef ?? null,
      };
      return withPlan(state, updated);
    }

    if (event.type === 'plan.archived') {
      const payload = event.payload as PayloadOf<'plan.archived'>;
      const existing = state.byId.get(payload.planId);
      if (!existing || existing.archived) return state;
      // Events remain in the log, so archiving hides a plan without deleting its history.
      return withPlan(state, { ...existing, archived: true });
    }

    if (event.type === 'plan.surfaced') {
      const payload = event.payload as PayloadOf<'plan.surfaced'>;
      const existing = state.byId.get(payload.planId);
      // I1 logged placeholder surfacings before plans existed; retain those log
      // events but ignore them here rather than crashing or inventing a plan.
      if (!existing) return state;
      return withPlan(state, {
        ...existing,
        surfacedCount: existing.surfacedCount + 1,
        lastSurfacedId: event.id,
      });
    }

    const payload = (event as TypedEvent<'catch.resolved'>).payload as PayloadOf<'catch.resolved'>;
    if (payload.resolution !== 'redirect' || !payload.planId) return state;
    const existing = state.byId.get(payload.planId);
    if (!existing) return state;
    // Only a redirect is choosing the offered plan; the other outcomes are the
    // deliberately useful "offered and not taken" signal for revision.
    return withPlan(state, { ...existing, chosenCount: existing.chosenCount + 1 });
  },
};

/** Active plans retain Map insertion order, which is creation order. */
export function activePlans(state: PlansState): Plan[] {
  return Array.from(state.byId.values()).filter((plan) => !plan.archived);
}

/**
 * The plan to offer on a resolution screen.
 *
 * Context is a **preference, not a filter**. The chain is: a plan whose time
 * bucket matches now → a plan tied to no particular time → any active plan at
 * all. Only an empty or fully archived set yields null.
 *
 * That last fallback matters more than it looks. Without it, someone whose
 * three plans all sit in morning, afternoon and evening gets *no plan* at
 * 00:30 — the screen where they most need something of their own to look at is
 * the screen where nothing appears. A plan slightly out of its hour is not a
 * mistake; spec §6.2 asks the resolution screen to show the user's own plan,
 * and matching is how we choose between several, not a condition for showing
 * one.
 */
export function matchingPlan(state: PlansState, at: Date | string): Plan | null {
  const active = activePlans(state);
  if (active.length === 0) return null;

  const inContext = active.filter((plan) => plan.context !== 'any' && contextMatches(plan.context, at));
  const anytime = active.filter((plan) => plan.context === 'any');
  const candidates = inContext.length > 0 ? inContext : anytime.length > 0 ? anytime : active;
  if (candidates.length === 0) return null;

  // A fixed first plan would hide the others and make one plan's surfacedCount
  // meaningless as a revision signal, so offers rotate with total surfacings.
  const totalSurfacings = Array.from(state.byId.values())
    .reduce((total, plan) => total + plan.surfacedCount, 0);
  return candidates[totalSurfacings % candidates.length] ?? null;
}

export function planNeedsRevision(plan: Plan, thresholds: PlanRevisionThresholds): boolean {
  // Spec §6.3 forbids asking after a single miss, even if a caller misconfigures a threshold.
  if (thresholds.minSurfacings <= 1) return false;
  return plan.surfacedCount >= thresholds.minSurfacings && plan.chosenCount === 0;
}

function withPlan(state: PlansState, updated: Plan): PlansState {
  const byId = new Map(state.byId);
  byId.set(updated.planId, updated);
  return { byId };
}
